import { asc, eq, getTableColumns, sql } from "drizzle-orm";

import type { CallConfiguration as WsCallConfiguration } from "@storyboard/web-client";
import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import { executeBuiltQuery, Tx } from "@/db/drizzleUtils";
import { callConfiguration } from "@/db/schema";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";

export async function createCallConfiguration(record: WsCallConfiguration, state: "queued" | "ringing", tx?: Tx) {
  const data: typeof callConfiguration.$inferSelect = {
    id: record.callId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    fromAlias: record.from.alias,
    fromAccountId: record.from.accountId,
    fromToken: record.from.token,
    fromDisplayName: record.from.displayName,
    toAlias: record.to.alias,
    toAccountId: record.to.accountId,
    toToken: record.to.token,
    toDisplayName: record.to.displayName,
    initiate: record.initiate,
    state: state,
  };

  const result = await executeBuiltQuery(
    db.insert(callConfiguration).values(data).onConflictDoNothing().returning(),
    "createCallConfiguration",
    tx,
  );
  emitTableUpdates(["call_configuration"]);
  return result;
}

export const fetchCallConfigurations = () => {
  return db
    .select({ ...getTableColumns(callConfiguration) })
    .from(callConfiguration)
    .where(eq(callConfiguration.state, "queued"))
    .orderBy(asc(callConfiguration.createdAt))
    .limit(2);
};

export type CallConfigurationState = (typeof callConfiguration)["state"]["_" & never] extends never
  ? (typeof callConfiguration.$inferInsert)["state"]
  : never;

export async function updateCallState(id: string, state: CallConfigurationState): Promise<void> {
  await db.update(callConfiguration).set({ state }).where(eq(callConfiguration.id, id)).execute();
  emitTableUpdates(["call_configuration"]);
}

export async function bulkUpsertCallConfiguration(records: WsCallConfiguration[]) {
  if (records.length === 0) return;
  const rows = records.map((record) => ({
    id: record.callId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    fromAlias: record.from.alias,
    fromAccountId: record.from.accountId,
    fromToken: record.from.token,
    fromDisplayName: record.from.displayName,
    toAlias: record.to.alias,
    toAccountId: record.to.accountId,
    toToken: record.to.token,
    toDisplayName: record.to.displayName,
    initiate: record.initiate,
    state: "queued",
  }));
  await bulkInsert(callConfiguration, rows, {
    onConflict: {
      type: "doUpdate",
      target: [callConfiguration.id],
      set: {
        updatedAt: sql`excluded."updatedAt"`,
        fromAlias: sql`excluded."fromAlias"`,
        fromAccountId: sql`excluded."fromAccountId"`,
        fromToken: sql`excluded."fromToken"`,
        fromDisplayName: sql`excluded."fromDisplayName"`,
        toAlias: sql`excluded."toAlias"`,
        toAccountId: sql`excluded."toAccountId"`,
        toToken: sql`excluded."toToken"`,
        toDisplayName: sql`excluded."toDisplayName"`,
        initiate: sql`excluded."initiate"`,
        state: sql`excluded."state"`,
      },
    },
  });
  emitTableUpdates(["call_configuration"]);
}
